import * as React from 'react';
import _debounce from '../../../utils/debounce';
import CSS from 'csstype';
import { H } from '../../layout';
import { Color } from '../../../styles/colors';
import MaskedInput from 'react-text-mask';
import Text from '../../text';
import handleEnterKey from '../../../utils/handle-enter-key';
import css from './input.module.css';
import { INPUT_MASKING_ENABLED } from '../../../config';
import { trackClick } from '../../../utils/analytics';
import { connect, ConnectedProps } from 'react-redux';
import { DEV } from '../../../config';
import { Dispatch } from '../../../types/app.types';

type Props = PropsFromRedux & {
  name: string;
  label?: string;
  initialValue?: string | null | undefined;
  value?: string;
  error?: (string | null | undefined) | (boolean | null | undefined);
  type: 'text' | 'tel' | 'password';
  required?: boolean;
  onChange?: (arg0: string) => any;
  debounce?: number;
  // ms to wait before calling onChange,
  autoFocus?: boolean;
  readOnly?: boolean;
  hint?: string | null | undefined;
  icon?: React.ReactNode;
  onIconClick?: (...args: Array<any>) => any;
  tabIndex?: number;
  maxLength?: number;
  style?: CSS.Properties<string | number>;
  inputStyle?: CSS.Properties<string | number>;
  labelStyle?: CSS.Properties<string | number>;
  mask?: string;
  nextFocus?: string;
  autoComplete?: string;
  handleFocus?: () => void;
  handleBlur?: () => void;
  placeholder?: string;
  disabled?: boolean;
  select?: boolean;
  className?: string;
  showCharCount?: boolean;
  // Requires non-null maxLength prop,
  user1stId?: string;
};
type State = {
  value: string;
  changed: boolean;
  focused: boolean;
  parsedMask: Array<RegExp | string>;
};

function parseMask(mask: string): Array<RegExp | string> {
  return mask.split('').map((char) => {
    if (char === '_') return /\d/;
    return char;
  });
}

export function Label({
  children,
  htmlFor,
  style,
  className,
}: {
  children?: React.ReactNode;
  htmlFor?: string;
  style: Record<string, any>;
  className?: string;
}) {
  /*eslint-disable jsx-a11y/label-has-for*/
  return (
    <label htmlFor={htmlFor} className={`${css.label}${className ? ` ${className}` : ''}`} style={style}>
      {children}
    </label>
  );
  /*eslint-enable jsx-a11y/label-has-for*/
}

export function Hint({ children, style }: { children?: React.ReactNode; style?: CSS.Properties<string | number> }) {
  return (
    <span className={css.hint} style={style} data-user1st-id="hint">
      {children}
    </span>
  );
}

class Input extends React.Component<Props, State> {
  state = {
    value: '',
    changed: false,
    focused: false,
    parsedMask: [],
  };

  constructor(props: Props) {
    super(props);
    this.input = React.createRef<HTMLInputElement & MaskedInput>();

    if (this.props.maxLength && this.props.mask && DEV) {
      Promise.reject("Can't use mask and maxLength");
    }

    if (props.onChange) {
      if (props.debounce) {
        this.callOnChangeProp = _debounce(props.onChange, props.debounce);
      } else {
        this.callOnChangeProp = this.props.onChange;
      }
    }
  }

  input: React.RefObject<HTMLInputElement & MaskedInput>;
  // this method is overwritten by the constructor
  callOnChangeProp: (arg0: string) => void;
  handleFocus: (...args: Array<any>) => any = () => {
    if (!this.input || !this.input?.current) return;
    if (typeof this.input?.current.focus === 'function') this.input?.current.focus();
    else if (this.input?.current?.inputElement && typeof this.input?.current?.inputElement.focus === 'function')
      this.input?.current?.inputElement.focus();
    this.setState({
      focused: true,
    });
    if (typeof this.props.handleFocus === 'function') this.props.handleFocus();
  };
  handleBlur: (...args: Array<any>) => any = () => {
    if (!this.input?.current) return;
    if (typeof this.input?.current.blur === 'function') this.input?.current.blur();
    else if (this.input?.current.inputElement && typeof this.input?.current.inputElement.blur === 'function')
      this.input?.current.inputElement.blur();
    this.setState({
      focused: false,
    });
    if (typeof this.props.handleBlur === 'function') this.props.handleBlur();
  };
  trackAndFocus = (name: string) => {
    this.props.dispatch(trackClick(name, 'textfield'));
    this.handleFocus();
  };
  handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState(
      {
        value: e.target.value,
        changed: true,
      },
      () => {
        this.callOnChangeProp && this.callOnChangeProp(this.state.value);
      }
    );
    const { nextFocus, mask } = this.props;

    if (nextFocus) {
      const maxLength = this.props.maxLength || (mask && mask.replace(' ', '').length);
      const currentLength = e.target.value.replace('\u2000', '').length;

      if (maxLength && currentLength === maxLength) {
        const matchedElements = document.getElementsByName(nextFocus);

        if (matchedElements.length > 0) {
          matchedElements[0].focus();
        }
      }
    }
  };

  componentDidMount() {
    const { initialValue, value, mask, autoFocus } = this.props;
    const stateUpdate = {
      parsedMask: [],
      value: '',
    };
    stateUpdate.parsedMask = mask ? parseMask(mask) : [];

    if (initialValue) {
      stateUpdate.value = initialValue;
    } else if (value) {
      stateUpdate.value = value;
    }

    if (mask || initialValue || value) this.setState(stateUpdate);

    if (autoFocus) {
      this.handleFocus();
    }
  }

  UNSAFE_componentWillUpdate(newProps: Props) {
    if (newProps) {
      // if the mask has changed update the parsed masked
      if (newProps.mask && newProps.mask !== this.props.mask)
        this.setState({
          parsedMask: parseMask(newProps.mask),
        });

      // if we have a new initial value and our old initial value is still being used, allow a switch
      if (
        newProps.initialValue &&
        newProps.initialValue !== this.props.initialValue &&
        this.props.initialValue === this.state.value
      ) {
        this.setState({
          value: newProps.initialValue,
        });
      }

      // if the value has changed update it
      if (typeof newProps.value === 'string' && newProps.value !== this.props.value) {
        this.setState({
          value: newProps.value,
        });
      }

      // if a new error set changed to false so we can clear it
      if (newProps.error && newProps.error !== this.props.error)
        this.setState({
          changed: false,
        });
    }
  }

  handleKeyDown(e: React.KeyboardEvent<any>, id: string, select: boolean | null | undefined) {
    if (!select) return;
    const tabKeyCode = e.keyCode === 9 || e.which === 9;
    const shiftKeyCode = e.keyCode === 16 || e.which === 16;
    const shiftTab = (tabKeyCode && e.shiftKey) || shiftKeyCode;
    const el = document.getElementById(id)?.nextSibling as HTMLDivElement;
    if (el && !shiftTab) el.focus();
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.autoFocus && prevProps.autoFocus !== this.props.autoFocus) this.handleFocus();
  }

  render() {
    const {
      name,
      type,
      label,
      icon,
      onIconClick,
      error,
      hint,
      readOnly,
      tabIndex,
      maxLength,
      mask,
      style,
      autoComplete,
      disabled,
      className,
      showCharCount,
      user1stId,
      required,
    } = this.props;
    const { changed, value, focused, parsedMask } = this.state;
    let inputClass: string = css.input;

    if (error && !changed) {
      inputClass = css.inputWithError;
    } else if (focused && !readOnly && !disabled) {
      inputClass = css.inputFocused;
    } else if (value) {
      inputClass = css.inputWithValue;
    }

    const inputProps = {
      id: name,
      'aria-label': name,
      className: css.field,
      maxLength,
      tabIndex,
      readOnly,
      name,
      type,
      onChange: this.handleChange,
      onFocus: this.handleFocus,
      onBlur: this.handleBlur,
      value: this.state.value,
      style: {
        marginTop: label ? 6 : null,
        ...this.props.inputStyle,
      },
      autoComplete,
      placeholder: this.props.placeholder || '',
      ref: this.input,
      disabled,
    };
    const select = this.props.select || false;
    const selectId = 'select-' + Math.floor(Math.random() * 10000);

    /* We disable the accesability rules here because we don't want this div to be tabbale */

    /*eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/interactive-supports-focus*/
    return (
      <div
        className={`${inputClass}${className ? ` ${className}` : ''}`}
        onClick={() => this.trackAndFocus(name)}
        onKeyDown={(e) => this.handleKeyDown(e, selectId, select)}
        style={style}
        role="button"
        data-qa={name}
        id={selectId}
        data-user1st-id={user1stId}
      >
        {label && (
          <Label htmlFor={name} style={this.props.labelStyle || {}}>
            {label}
          </Label>
        )}
        {/*eslint-enable jsx-a11y/click-events-have-key-events, jsx-a11y/interactive-supports-focus*/}
        {INPUT_MASKING_ENABLED && mask ? (
          <MaskedInput
            guide={true}
            keepCharPositions={true} // never change this to placeholderChar="\u2000" because babel will escape this breaking the mask
            placeholderChar={'\u2000'}
            mask={parsedMask}
            {...inputProps}
          />
        ) : (
          <input {...inputProps} />
        )}
        {icon && (
          <span
            className={select ? (label ? css.iconSelect : css.iconSelectNoLabel) : css.icon}
            onClick={onIconClick}
            role="button"
            tabIndex={0}
            onKeyDown={handleEnterKey(onIconClick)}
          >
            {icon}
          </span>
        )}
        <H justifyContent="space-between">
          <Hint
            style={
              !!maxLength && !!showCharCount
                ? {
                    marginTop: 4,
                  }
                : undefined
            }
          >
            {error && !changed ? error : hint}
          </Hint>
          {maxLength && showCharCount && (
            <Text className="mt-sm-4" type="body3-blinkui" color={Color.tertiary80}>{`${
              value ? value.length : '0'
            } / ${maxLength}`}</Text>
          )}
        </H>
        {required && (
          <Hint
            style={{
              fontSize: 12,
              fontWeight: 300,
              color: '#6c6f72',
            }}
          >
            Required
          </Hint>
        )}
      </div>
    );
  }
}

const connector = connect(null, (dispatch: Dispatch) => ({ dispatch }), null, {
  forwardRef: true,
});

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(Input);
